// Подсказка на Radix: заголовок жирным, строки, акцент цветом. Нативный title остаётся там, где одно
// слово (кнопки-иконки); всё, что несёт смысл, — здесь. Provider — в app.tsx. На триггере data-tip с текстом
// подсказки: для e2e и для тех, кто читает DOM.
import * as T from '@radix-ui/react-tooltip';
import { forwardRef, type ComponentPropsWithoutRef, type ReactElement, type ReactNode } from 'react';
import { cn } from './cn';

export type TipTone = 'muted' | 'warn' | 'danger' | 'ok';
export type TipLine = string | { text: string; tone?: TipTone };

const TONE: Record<TipTone, string> = { muted: 'text-gray-500', warn: 'text-amber-700', danger: 'text-red-700', ok: 'text-green-700' };

type Props = Omit<ComponentPropsWithoutRef<typeof T.Trigger>, 'title' | 'asChild'> & {
  title?: ReactNode;      // первая строка, жирная
  lines?: TipLine[];      // пустые строки пропускаются
  hint?: string;          // приглушённая последняя строка: что можно сделать
  side?: 'top' | 'bottom' | 'left' | 'right';
  disabled?: boolean;     // не показывать (тащат): обёртка остаётся, иначе элемент пересоздаётся и теряет фокус
  children: ReactElement;
};

const text = (l: TipLine) => (typeof l === 'string' ? l : l.text);

// Radix закрывает подсказку по Escape и ставит defaultPrevented — как меню. Но подсказка не меню: тот же
// Escape должен ещё снять выделение или подсветку. Помечаем событие, чтобы useEscape его не отбрасывал.
let tooltipEscape: Event | null = null;
export const escapeClosedTooltipOnly = (e: Event): boolean => e === tooltipEscape;

export const Tooltip = forwardRef<HTMLButtonElement, Props>(function Tooltip({ title, lines = [], hint, side = 'top', disabled = false, children, ...rest }, ref) {
  const shown = lines.filter((l) => text(l));
  const plain = [typeof title === 'string' ? title : '', ...shown.map(text), hint ?? ''].filter(Boolean).join('\n');
  return (
    <T.Root open={disabled ? false : undefined}>
      <T.Trigger asChild ref={ref} data-tip={plain} {...rest}>{children}</T.Trigger>
      <T.Portal>
        {/* белая, как контекстное меню: одна семья всплывающих панелей */}
        <T.Content side={side} sideOffset={6} collisionPadding={8} onEscapeKeyDown={(e) => { tooltipEscape = e; }} className="z-[70] max-w-xs rounded-md bg-white text-gray-900 border border-gray-300 text-[13px] leading-[18px] px-3 py-2 shadow-lg select-none">
          {title && <div className="font-semibold text-sm leading-5">{title}</div>}
          {shown.map((l, i) => <div key={i} className={cn(typeof l !== 'string' && l.tone && TONE[l.tone])}>{text(l)}</div>)}
          {hint && <div className="text-gray-500 mt-0.5">{hint}</div>}
        </T.Content>
      </T.Portal>
    </T.Root>
  );
});

/** Провайдер задержек: один на приложение. Подсказки не ловят мышь — они только читаются. */
export const TooltipProvider = ({ children }: { children: ReactNode }) => <T.Provider delayDuration={450} skipDelayDuration={250} disableHoverableContent>{children}</T.Provider>;
